import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { Trans, useLingui } from "@lingui/react/macro";
import { YesChefLogo } from "@/components/common/YesChefLogo";
import { UserMenu } from "@/components/layout/UserMenu";
import { useNavigate } from "@tanstack/react-router";
import { Check, Loader2 } from "lucide-react";
import type { ReactNode } from "react";

type StepStatus = "completed" | "active" | "pending";

const STEPS: { label: MessageDescriptor; to: string }[] = [
  { label: msg`Organization Setup`, to: "/onboarding/organization" },
  { label: msg`Add Concept`,        to: "/onboarding/company" },
  { label: msg`Add Region`,         to: "/onboarding/regions" },
  { label: msg`Add Location`,       to: "/onboarding/locations" },
  { label: msg`Invite Users`,       to: "/onboarding/invite" },
  { label: msg`Choose a Plan`,      to: "/onboarding/plan" },
];

function StepCircle({ status, number }: { status: StepStatus; number: number }) {
  if (status === "completed") {
    return (
      <div
        className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 bg-(--color-status-active)"
      >
        <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
      </div>
    );
  }
  if (status === "active") {
    return (
      <div
        className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 border-[2px] border-(--brand-black)"
      >
        <Loader2 className="w-3 h-3 animate-spin text-(--brand-black)" />
      </div>
    );
  }
  return (
    <div
      className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-xs font-medium bg-(--gray-200) text-(--gray-500)"
    >
      {number}
    </div>
  );
}

interface OnboardingLayoutProps {
  currentStep: number;
  children: ReactNode;
}

export { STEPS };

export function OnboardingLayout({ currentStep, children }: OnboardingLayoutProps) {
  const { t } = useLingui();
  const navigate = useNavigate();

  return (
    <div className="flex flex-col min-h-screen bg-(image:--color-surface-onboarding)">
      {/* Top header */}
      <header
        className="flex items-center justify-between px-8 shrink-0"
        style={{
          height: "4.5rem",
          background: "#fff",
          borderBottom: "1px solid var(--gray-200)",
        }}
      >
        <YesChefLogo />
        <UserMenu showOrgSwitcher={false} showPreferences={false} showProfile={false} />
      </header>

      {/* Body */}
      <div className="flex flex-1">
        {/* Left sidebar */}
        <aside className="shrink-0 flex flex-col gap-5 pt-10 px-7" style={{ width: "15rem" }}>
          <span
            className="text-xs font-semibold uppercase tracking-widest text-(--gray-500)"
          >
            <Trans>Account Setup</Trans>
          </span>
          <div className="flex flex-col">
            {STEPS.map(({ label, to }, i) => {
              const status: StepStatus =
                i < currentStep ? "completed" : i === currentStep ? "active" : "pending";
              const clickable = status === "completed";
              return (
                <div key={to}>
                  <div
                    className="flex items-center gap-3 py-0.5 rounded"
                    style={{ cursor: clickable ? "pointer" : "default" }}
                    onClick={clickable ? () => navigate({ to }) : undefined}
                  >
                    <StepCircle status={status} number={i + 1} />
                    <span
                      className="text-sm transition-opacity"
                      style={{
                        color: status === "pending" ? "var(--gray-400)" : "var(--brand-black)",
                        fontWeight: status === "active" ? 600 : 400,
                      }}
                    >
                      {t(label)}
                    </span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div
                      className="w-px h-5"
                      style={{
                        marginLeft: "0.6875rem",
                        background: i < currentStep ? "var(--color-status-active)" : "var(--gray-200)",
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 flex justify-center pt-12 pb-12 px-6">
          <div className="w-full" style={{ maxWidth: "37.5rem" }}>
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
